'use client';

import { c as _c } from "react/compiler-runtime";
import { Button } from '@algoacademy/design-system/web/actions/Button';
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon, ChevronUpIcon } from '@heroicons/react/24/solid';
import { useEffect, useRef, useState } from 'react';
import { DESKTOP_COLLAPSED_PROOF_COUNT, type Proof } from './data';
import { ProofCard } from './ProofCard';
const LONG_PROOF_LENGTH = 400;

/**
 * Derived from the list rather than hardcoded indices, so adding or reordering a
 * testimonial cannot silently drop it from the mobile carousel. Long testimonials
 * get a slide to themselves; short ones are paired up.
 */
function buildMobileSlides(items: Proof[]) {
  const slides: Proof[][] = [];
  let pending: Proof[] = [];
  const flushPending = () => {
    if (pending.length > 0) {
      slides.push(pending);
      pending = [];
    }
  };
  for (const proof of items) {
    if (proof.content.length > LONG_PROOF_LENGTH) {
      flushPending();
      slides.push([proof]);
      continue;
    }
    pending.push(proof);
    if (pending.length === 2) flushPending();
  }
  flushPending();
  return slides;
}
function balanceColumns(items: Proof[], columnCount: number) {
  const columns = Array.from({
    length: columnCount
  }, () => ({
    items: [] as Proof[],
    weight: 0
  }));
  for (const proof of items) {
    const shortestColumn = columns.reduce((shortest, column) => column.weight < shortest.weight ? column : shortest);
    const textLines = Math.ceil(proof.content.length / 38);
    const chromeWeight = proof.kind === 'chat' ? 210 : 175;
    shortestColumn.items.push(proof);
    shortestColumn.weight += chromeWeight + textLines * 22;
  }
  return columns;
}
export function ProofWall(t0) {
  const $ = _c(24);
  const {
    proofs
  } = t0;
  const [showAll, setShowAll] = useState(false);
  const [columnCount, setColumnCount] = useState(4);
  const carouselRef = useRef(null);
  const desktopWallRef = useRef(null);
  const visibleProofs = showAll ? proofs : proofs.slice(0, DESKTOP_COLLAPSED_PROOF_COUNT);
  const hasMore = proofs.length > DESKTOP_COLLAPSED_PROOF_COUNT;
  const desktopColumns = balanceColumns(visibleProofs, columnCount);
  const mobileSlides = buildMobileSlides(proofs);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const wall = desktopWallRef.current;
      if (!wall) {
        return;
      }
      const updateColumnCount = () => {
        setColumnCount(Math.max(2, Math.min(4, Math.floor(wall.clientWidth / 285))));
      };
      const observer = new ResizeObserver(updateColumnCount);
      updateColumnCount();
      observer.observe(wall);
      return () => observer.disconnect();
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = direction => {
      const carousel = carouselRef.current;
      if (!carousel) {
        return;
      }
      const slides = Array.from(carousel.querySelectorAll("[data-proof-slide]"));
      if (slides.length === 0) {
        return;
      }
      const viewportCenter = carousel.scrollLeft + carousel.clientWidth / 2;
      const currentIndex = slides.reduce((closest, slide, index) => Math.abs(slide.offsetLeft + slide.offsetWidth / 2 - viewportCenter) < Math.abs(slides[closest].offsetLeft + slides[closest].offsetWidth / 2 - viewportCenter) ? index : closest, 0);
      const nextIndex = (currentIndex + direction + slides.length) % slides.length;
      slides[nextIndex].scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center"
      });
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const moveCarousel = t3;
  const t4 = desktopColumns.map(_temp2);
  let t5;
  if ($[3] !== t4) {
    t5 = <div ref={desktopWallRef} className="flex w-full items-start gap-[18px]">{t4}</div>;
    $[3] = t4;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== hasMore || $[6] !== showAll) {
    t6 = hasMore ? <div className="mt-9 flex justify-center">{showAll ? <Button outline={true} size="lg" aria-expanded="true" onClick={() => setShowAll(false)} className="gap-2 px-6 py-3 shadow-sm">Schowaj opinie<ChevronUpIcon data-slot="icon" /></Button> : <Button size="lg" aria-expanded="false" onClick={() => setShowAll(true)} className="gap-2 px-6 py-3 shadow-sm">Pokaż wszystkie opinie<ChevronDownIcon data-slot="icon" /></Button>}</div> : null;
    $[5] = hasMore;
    $[6] = showAll;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== t5 || $[9] !== t6) {
    t7 = <div className="hidden sm:block">{t5}{t6}</div>;
    $[8] = t5;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const t8 = "relative -mx-4 sm:hidden";
  let t9;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => moveCarousel(-1);
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <button type="button" aria-label="Poprzednia opinia" onClick={t9} className="absolute top-1/2 left-1 z-10 flex size-11 -translate-y-1/2 items-center justify-center rounded-full border border-borderPrimary bg-backgroundPrimary text-contentPrimary shadow-md"><ChevronLeftIcon className="size-5" /></button>;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => moveCarousel(1);
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  let t12;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <button type="button" aria-label={"Nast\u0119pna opinia"} onClick={t11} className="absolute top-1/2 right-1 z-10 flex size-11 -translate-y-1/2 items-center justify-center rounded-full border border-borderPrimary bg-backgroundPrimary text-contentPrimary shadow-md"><ChevronRightIcon className="size-5" /></button>;
    $[14] = t12;
  } else {
    t12 = $[14];
  }
  const t13 = "snap-x snap-mandatory overflow-x-auto scroll-smooth [scrollbar-width:none] [&::-webkit-scrollbar]:hidden";
  const t14 = "flex items-start gap-4 px-[9vw] pb-2";
  const t15 = mobileSlides.map(_temp4);
  let t16;
  if ($[15] !== t15) {
    t16 = <div ref={carouselRef} className={t13}><div className={t14}>{t15}</div></div>;
    $[15] = t15;
    $[16] = t16;
  } else {
    t16 = $[16];
  }
  let t17;
  if ($[17] !== t10 || $[18] !== t12 || $[19] !== t16) {
    t17 = <div className={t8}>{t10}{t12}{t16}</div>;
    $[17] = t10;
    $[18] = t12;
    $[19] = t16;
    $[20] = t17;
  } else {
    t17 = $[20];
  }
  let t18;
  if ($[21] !== t17 || $[22] !== t7) {
    t18 = <div>{t7}{t17}</div>;
    $[21] = t17;
    $[22] = t7;
    $[23] = t18;
  } else {
    t18 = $[23];
  }
  return t18;
}
function _temp4(slide_0) {
  return <div key={slide_0[0]?.id} data-proof-slide={true} className="w-[82vw] shrink-0 snap-center space-y-4">{slide_0.map(_temp3)}</div>;
}
function _temp3(proof_0) {
  return <ProofCard key={proof_0.id} proof={proof_0} />;
}
function _temp2(column) {
  return <div key={column.items[0]?.id} className="flex min-w-0 flex-1 flex-col gap-[18px]">{column.items.map(_temp)}</div>;
}
function _temp(proof) {
  return <ProofCard key={proof.id} proof={proof} className="w-full" />;
}